/**
 * User Service - Handles API interactions for user management and authentication
 */

import { get, post, put, del } from '../utils/apiUtils';

const API_URL = '/api/v1/persistence/users';
const AUTH_URL = '/api/v1/authentication';

/**
 * Get all users with optional filtering
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of users
 */
export const getUsers = async (filters = {}) => {
  try {
    const response = await get(API_URL, { params: filters });
    return response;
  } catch (error) {
    console.error('Error fetching users:', error);
    throw error;
  }
};

/**
 * Get a specific user by ID
 * @param {string} userId - User ID
 * @returns {Promise<Object>} Promise resolving to user object
 */
export const getUserById = async (userId) => {
  try {
    const response = await get(`${API_URL}/${userId}`);
    return response;
  } catch (error) {
    console.error(`Error fetching user ${userId}:`, error);
    throw error;
  }
};

/**
 * Create a new user
 * @param {Object} userData - User data
 * @returns {Promise<Object>} Promise resolving to created user
 */
export const createUser = async (userData) => {
  try {
    const response = await post(API_URL, userData);
    return response;
  } catch (error) {
    console.error('Error creating user:', error);
    throw error;
  }
};

/**
 * Update an existing user
 * @param {string} userId - User ID
 * @param {Object} userData - Updated user data
 * @returns {Promise<Object>} Promise resolving to updated user
 */
export const updateUser = async (userId, userData) => {
  try {
    const response = await put(`${API_URL}/${userId}`, userData);
    return response;
  } catch (error) {
    console.error(`Error updating user ${userId}:`, error);
    throw error;
  }
};

/**
 * Delete a user
 * @param {string} userId - User ID
 * @returns {Promise<void>} Promise resolving when user is deleted
 */
export const deleteUser = async (userId) => {
  try {
    await del(`${API_URL}/${userId}`);
  } catch (error) {
    console.error(`Error deleting user ${userId}:`, error);
    throw error;
  }
};

/**
 * Get user roles
 * @returns {Promise<Array>} Promise resolving to array of roles
 */
export const getUserRoles = async () => {
  try {
    const response = await get(`${API_URL}/roles`);
    return response;
  } catch (error) {
    console.error('Error fetching user roles:', error);
    throw error;
  }
};

/**
 * Assign a role to a user
 * @param {string} userId - User ID
 * @param {string} roleId - Role ID
 * @returns {Promise<Object>} Promise resolving to updated user
 */
export const assignUserRole = async (userId, roleId) => {
  try {
    const response = await post(`${API_URL}/${userId}/roles`, { roleId });
    return response;
  } catch (error) {
    console.error(`Error assigning role to user ${userId}:`, error);
    throw error;
  }
};

/**
 * Remove a role from a user
 * @param {string} userId - User ID
 * @param {string} roleId - Role ID
 * @returns {Promise<Object>} Promise resolving to updated user
 */
export const removeUserRole = async (userId, roleId) => {
  try {
    const response = await del(`${API_URL}/${userId}/roles/${roleId}`);
    return response;
  } catch (error) {
    console.error(`Error removing role from user ${userId}:`, error);
    throw error;
  }
};

/**
 * Get user permissions
 * @param {string} userId - User ID
 * @returns {Promise<Array>} Promise resolving to array of permissions
 */
export const getUserPermissions = async (userId) => {
  try {
    const response = await get(`${API_URL}/${userId}/permissions`);
    return response;
  } catch (error) {
    console.error(`Error fetching permissions for user ${userId}:`, error);
    throw error;
  }
};

/**
 * Get user activity log
 * @param {string} userId - User ID
 * @param {Object} filters - Filter parameters
 * @returns {Promise<Array>} Promise resolving to array of activity logs
 */
export const getUserActivityLog = async (userId, filters = {}) => {
  try {
    const response = await get(`${API_URL}/${userId}/activity-log`, { params: filters });
    return response;
  } catch (error) {
    console.error(`Error fetching activity log for user ${userId}:`, error);
    throw error;
  }
};

/**
 * Get current authenticated user
 * @returns {Promise<Object>} Promise resolving to current user
 */
export const getCurrentUser = async () => {
  try {
    const response = await get(`${AUTH_URL}/me`);
    return response;
  } catch (error) {
    console.error('Error fetching current user:', error);
    throw error;
  }
};

/**
 * Login user
 * @param {Object} credentials - User credentials
 * @returns {Promise<Object>} Promise resolving to login response
 */
export const login = async (credentials) => {
  try {
    const response = await post(`${AUTH_URL}/login`, credentials);
    return response;
  } catch (error) {
    console.error('Error logging in:', error);
    throw error;
  }
};

/**
 * Logout user
 * @returns {Promise<void>} Promise resolving when user is logged out
 */
export const logout = async () => {
  try {
    await post(`${AUTH_URL}/logout`);
  } catch (error) {
    console.error('Error logging out:', error);
    throw error;
  }
};

/**
 * Register a new user
 * @param {Object} userData - User registration data
 * @returns {Promise<Object>} Promise resolving to registered user
 */
export const register = async (userData) => {
  try {
    const response = await post(`${AUTH_URL}/register`, userData);
    return response;
  } catch (error) {
    console.error('Error registering user:', error);
    throw error;
  }
};

/**
 * Request password reset
 * @param {Object} data - Password reset request data
 * @returns {Promise<Object>} Promise resolving to reset response
 */
export const requestPasswordReset = async (data) => {
  try {
    const response = await post(`${AUTH_URL}/forgot-password`, data);
    return response;
  } catch (error) {
    console.error('Error requesting password reset:', error);
    throw error;
  }
};

/**
 * Reset password
 * @param {Object} data - Password reset data
 * @returns {Promise<Object>} Promise resolving to reset response
 */
export const resetPassword = async (data) => {
  try {
    const response = await post(`${AUTH_URL}/reset-password`, data);
    return response;
  } catch (error) {
    console.error('Error resetting password:', error);
    throw error;
  }
};

/**
 * Change password
 * @param {Object} data - Password change data
 * @returns {Promise<Object>} Promise resolving to change response
 */
export const changePassword = async (data) => {
  try {
    const response = await post(`${AUTH_URL}/change-password`, data);
    return response;
  } catch (error) {
    console.error('Error changing password:', error);
    throw error;
  }
};

/**
 * Update user profile
 * @param {Object} profileData - Profile data
 * @returns {Promise<Object>} Promise resolving to updated profile
 */
export const updateProfile = async (profileData) => {
  try {
    const response = await put(`${AUTH_URL}/profile`, profileData);
    return response;
  } catch (error) {
    console.error('Error updating profile:', error);
    throw error;
  }
};

/**
 * Get user preferences
 * @returns {Promise<Object>} Promise resolving to user preferences
 */
export const getUserPreferences = async () => {
  try {
    const response = await get(`${AUTH_URL}/preferences`);
    return response;
  } catch (error) {
    console.error('Error fetching user preferences:', error);
    throw error;
  }
};

/**
 * Update user preferences
 * @param {Object} preferences - Updated preferences
 * @returns {Promise<Object>} Promise resolving to updated preferences
 */
export const updateUserPreferences = async (preferences) => {
  try {
    const response = await put(`${AUTH_URL}/preferences`, preferences);
    return response;
  } catch (error) {
    console.error('Error updating user preferences:', error);
    throw error;
  }
};

export default {
  getUsers,
  getUserById,
  createUser,
  updateUser,
  deleteUser,
  getUserRoles,
  assignUserRole,
  removeUserRole,
  getUserPermissions,
  getUserActivityLog,
  getCurrentUser,
  login,
  logout,
  register,
  requestPasswordReset,
  resetPassword,
  changePassword,
  updateProfile,
  getUserPreferences,
  updateUserPreferences
};
